package er.prateek.crossly.firebase

import er.prateek.crossly.config.AppConfig

/** Wraps Firebase Remote Config. [fetchAndActivate] is a no-op on platforms with no remote client. */
interface RemoteConfigClient {
    /** Seeds [RemoteConfigDefaults] as the pre-fetch fallback values. */
    suspend fun setDefaults()
    suspend fun fetchAndActivate(): Boolean
    fun getBoolean(key: String): Boolean
    fun getLong(key: String): Long
    fun getString(key: String): String
}

/** Sets defaults, then fetches+activates once. Safe to call repeatedly; cheap on a no-op client. */
suspend fun RemoteConfigClient.warmUp() {
    setDefaults()
    fetchAndActivate()
}

/** Flag names, matching docs/PLAN.md's "Firebase analytics events" / feature-flag list. */
object RemoteConfigKeys {
    const val QUIZZES_ENABLED = "quizzes_enabled"
    const val HINTS_MAX_PER_PUZZLE = "hints_max_per_puzzle"
    const val TIER_UNLOCK_THRESHOLD_PCT = "tier_unlock_threshold_pct"

    /**
     * The app's whole remotely-tunable configuration as one JSON blob, organised into named
     * sections -- see [AppConfig]. One parameter instead of a dozen, so adding a knob (or a
     * whole new section) never means adding a Remote Config parameter that shipped builds
     * don't read.
     */
    const val APP_CONFIG = "app_config"
}

/**
 * Compiled-in fallbacks, used verbatim on platforms with no remote client (JVM desktop,
 * wasmJs) and as the seed defaults on platforms that do fetch, so behavior is identical
 * before the first successful fetch on every platform.
 */
object RemoteConfigDefaults {
    const val QUIZZES_ENABLED = true
    const val HINTS_MAX_PER_PUZZLE = 3L
    const val TIER_UNLOCK_THRESHOLD_PCT = 75L

    /**
     * Serialized from [AppConfig.DEFAULT] rather than written out by hand, so the compiled
     * fallback can never drift from the model's own defaults.
     */
    val APP_CONFIG: String get() = AppConfig.defaultJson()
}

/** No remote client on this platform (JVM desktop, wasmJs) -- always returns compiled defaults. */
object CompiledDefaultsRemoteConfigClient : RemoteConfigClient {
    override suspend fun setDefaults() = Unit
    override suspend fun fetchAndActivate(): Boolean = false

    override fun getBoolean(key: String): Boolean = when (key) {
        RemoteConfigKeys.QUIZZES_ENABLED -> RemoteConfigDefaults.QUIZZES_ENABLED
        else -> false
    }

    override fun getLong(key: String): Long = when (key) {
        RemoteConfigKeys.HINTS_MAX_PER_PUZZLE -> RemoteConfigDefaults.HINTS_MAX_PER_PUZZLE
        RemoteConfigKeys.TIER_UNLOCK_THRESHOLD_PCT -> RemoteConfigDefaults.TIER_UNLOCK_THRESHOLD_PCT
        else -> 0L
    }

    override fun getString(key: String): String = when (key) {
        RemoteConfigKeys.APP_CONFIG -> RemoteConfigDefaults.APP_CONFIG
        else -> ""
    }
}
